// Where a player a team takes in sits in its lineup. PLAN-2026-09-24 W5.
//
// A ROSTER THAT COUNTS RIGHT CAN STILL NOT FIT. `rosterProblem` counts: fifteen
// spots, fifteen players, legal. The lineup lays them out: a full bench and an
// open kicker's slot, with no kicker on the roster, has fifteen spots and
// nowhere to put a sixteenth running back — he would be on the roster and in
// no lineup cell, which on the Lineup screen is nowhere. This is the layout
// half, and the one answer every host asks: the simulator's add, the league
// store's add, a claim's placement and the waiver run (`WaiverContext.fits`).
//
// THE ONE RULE, which the simulator's `applyMove` seats by too: a player
// coming in takes an open bench cell; else an open starting slot he can play;
// else an open starting slot a bench player can play, who moves up and leaves
// his bench cell to the new man;
// else one a player in another starting slot can play, who moves over and
// leaves his slot to the new man when the new man can play it — the tight end
// in the flex to tight end, and the receiver coming in to the flex.
// A cell whose row is stamped (`locked_at`) is history and is never written; a
// player whose game has started is never moved.
//
// PURE AND SPORT-FREE, like `initialLineup`: the shape and the position lookup
// are handed in.

import type { RosterShape } from './legality';

/** One starter or bench cell of a team's lineup in a window. */
export interface LineupCell {
  slot: string;
  /** 0-based within the slot, as `lineup_slots.slot_index` counts. */
  slotIndex: number;
  playerId: string | null;
  /** The row is stamped: history, never written. */
  stamped: boolean;
}

/** Every starter and bench cell of the shape, in shape order, filled from the stored rows. */
export function lineupCells(shape: RosterShape, stored: readonly LineupCell[]): LineupCell[] {
  const byKey = new Map(stored.map((r) => [`${r.slot}|${r.slotIndex}`, r]));
  const out: LineupCell[] = [];
  for (const slot of shape.slots) {
    if (slot.kind !== 'starter' && slot.kind !== 'bench') continue;
    for (let i = 0; i < slot.count; i += 1) {
      const row = byKey.get(`${slot.id}|${i}`);
      out.push({
        slot: slot.id,
        slotIndex: i,
        playerId: row?.playerId ?? null,
        stamped: row?.stamped ?? false,
      });
    }
  }
  return out;
}

/**
 * Put one player in `cells`, mutating them: the bench, an open starting slot
 * he can play, an open starting slot a bench player moves up to, or one a
 * player in another starting slot moves over to, leaving that slot to him.
 * False when there is no such cell, and `cells` are then untouched.
 */
export function placePlayer(
  cells: LineupCell[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
  locked: (playerId: string) => boolean,
  playerId: string,
): boolean {
  const defs = new Map(shape.slots.map((s) => [s.id, s]));
  const plays = (slot: string, id: string) => {
    const def = defs.get(slot);
    return (
      def !== undefined && (def.eligible.length === 0 || def.eligible.includes(positionOf(id)))
    );
  };
  const kind = (c: LineupCell) => defs.get(c.slot)?.kind;
  const open = (c: LineupCell) => c.playerId === null && !c.stamped;
  const target =
    cells.find((c) => open(c) && kind(c) === 'bench') ??
    cells.find((c) => open(c) && kind(c) === 'starter' && plays(c.slot, playerId));
  if (target) {
    target.playerId = playerId;
    return true;
  }
  for (const empty of cells) {
    if (!open(empty) || kind(empty) !== 'starter') continue;
    const mover = cells.find(
      (c) =>
        c.playerId !== null &&
        !c.stamped &&
        kind(c) === 'bench' &&
        plays(empty.slot, c.playerId) &&
        !locked(c.playerId),
    );
    if (!mover) continue;
    empty.playerId = mover.playerId;
    mover.playerId = playerId;
    return true;
  }
  // FROM ANOTHER STARTING SLOT, with nobody on the bench who fits: a trade
  // that sent a team's tight end away for a receiver, with its other tight end
  // in the flex and a full bench, left the receiver in no cell of the week, and
  // a bot seated him after his kickoff (the season suite's bot league,
  // 2026-10-22). B-30.
  for (const empty of cells) {
    if (!open(empty) || kind(empty) !== 'starter') continue;
    const mover = cells.find(
      (c) =>
        c !== empty &&
        c.playerId !== null &&
        !c.stamped &&
        kind(c) === 'starter' &&
        plays(empty.slot, c.playerId) &&
        plays(c.slot, playerId) &&
        !locked(c.playerId),
    );
    if (!mover) continue;
    empty.playerId = mover.playerId;
    mover.playerId = playerId;
    return true;
  }
  return false;
}

/**
 * `roster` laid into a copy of `cells`: a player no longer on the roster leaves
 * his cell (unless it is stamped history), and each player on the roster with
 * no cell is placed in roster order (`placePlayer`). `unplaced` is the first
 * who could not be, or null when the roster fits.
 */
export function arrangeRoster(
  cells: readonly LineupCell[],
  shape: RosterShape,
  positionOf: (playerId: string) => string,
  locked: (playerId: string) => boolean,
  roster: readonly string[],
): { cells: LineupCell[]; unplaced: string | null } {
  const keep = new Set(roster);
  const out = cells.map((c) =>
    c.playerId !== null && !keep.has(c.playerId) && !c.stamped
      ? { ...c, playerId: null }
      : { ...c },
  );
  const seated = new Set(out.map((c) => c.playerId).filter((id): id is string => id !== null));
  for (const id of roster) {
    if (seated.has(id)) continue;
    if (!placePlayer(out, shape, positionOf, locked, id)) return { cells: out, unplaced: id };
    seated.add(id);
  }
  return { cells: out, unplaced: null };
}

/** The cells `after` changed from `before` to hold a player: what a host writes. */
export function seatedChanges(
  before: readonly LineupCell[],
  after: readonly LineupCell[],
): { slot: string; slotIndex: number; playerId: string }[] {
  const was = new Map(before.map((c) => [`${c.slot}|${c.slotIndex}`, c.playerId]));
  return after
    .filter((c) => c.playerId !== null && was.get(`${c.slot}|${c.slotIndex}`) !== c.playerId)
    .map((c) => ({ slot: c.slot, slotIndex: c.slotIndex, playerId: c.playerId! }));
}
